import React from "react";
import { Helmet } from "react-helmet";
import PageTitle from "../../components/PageTitle";
import { Settings } from "../../configuration/settings";
import Item from "../../models/item";
import RecomendedItems from "../../components/RecomendedItems";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import ModelLinks from "../../components/ModelLinks";
import { ModelDetails } from "../../configuration/model-description";

function Measurements(props: { items: Item[] }) {
    const topModels = Object.entries(ModelDetails)
        .sort(([, modelDetailsA], [, modelDetailsB]) => modelDetailsA.seoRank - modelDetailsB.seoRank)
        .map(([model, modelDetails]) => model)
        .slice(0, 4)
        .sort();

    return (
        <>
            <Helmet>
                <title>Як робити заміри джинсів Levi’s - {Settings.shopName}</title>
                <meta name="description" content={`Дізнайтесь, як правильно робити заміри джинсів Levi’s: талія, довжина, посадка. Покрокова інструкція та поради від ${Settings.shopName}.`} />
                <link rel="canonical" href={`${Settings.siteUrl}/yak-robyty-zamiry-dzhyns-levis/`} />
            </Helmet>
            <Breadcrumbs items={[ { text: "Як правильно робити заміри джинсів Levi’s"} ]} />
            <PageTitle title="Як правильно робити заміри джинсів Levi’s: перед покупкою" />
            <div className="text-start">
                 <p>
                    Щоб <strong>джинси Levi’s</strong> ідеально сиділи, важливо правильно
                    зняти заміри. Навіть якщо ви знаєте свій розмір, різні моделі та посадки можуть відрізнятися. 
                    У цій статті ми детально пояснюємо, <strong>як робити заміри джинсів Levi’s</strong> перед покупкою онлайн.
                </p>

                <h2 className="fs-5">Чому важливо знімати заміри</h2>
                <p>
                    Розміри джинсів Levi’s вказуються у форматі W - L, але фактична посадка
                    залежить від моделі, тканини та крою. Заміри допомагають:
                </p>
                <ul>
                    <li>уникнути помилки з розміром</li>
                    <li>підібрати комфортну посадку</li>
                    <li>зменшити ризик обміну або повернення</li>
                </ul>

                <h2 className="fs-5">Що знадобиться для замірів</h2>
                <ul>
                    <li>пара джинсів, які вам добре підходять</li>
                    <li>сантиметрова стрічка або лінійка</li>
                    <li>рівна поверхня</li>
                </ul>

                <h2 className="fs-5">Як міряти</h2>
                <p>
                    Пропонуємо скористатися нашою візуально інструкцією зі зняття вимірів джинс. 
                    Саме так ми робимо заміри, які ви можете знайти в описі кожного товару у нас на сайті. 
                    Сподіваємося, що ці вимірювання допоможуть вам максимально точно вибрати потрібний розмір.
                </p>
                <p>
                    Заміри завжди знімайте з джинсів у розкладеному та застебнутому вигляді.
                </p>
                <h3 className="fs-5">1. Напівпояс</h3>
                <p>
                    Для виміру напівпояса необхідно поєднати джинси так,
                    щоб передня та задня частина були на одному рівні, 
                    далі потрібно натягнути краї та зробити вимір від краю до краю виробу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/waistband.jpg" alt="Вимірювання напівпоясу джинсів Levi’s" />
                <h3 className="fs-5">2. Посадка</h3>
                <p>
                    Виміри передньої посадки необхідно проводити від самого перетину внутрішніх швів і 
                    до верху передньої частини виробу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/rise-front.jpg" alt="Вимірювання передньої посадки джинсів Levi’s"/>
                <p>
                    Виміри задньої посадки джинсів робляться так само, але зі зворотньої сторони. 
                    Необхідно перевернути джинси і міряти від перетину внутрішніх швів до верху задньої частини виробу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/rise-back.jpg" alt="Вимірювання задньої посадки джинсів Levi’s" />
                <h3 className="fs-5">3. Довжина</h3>
                <p>
                    Заміри внутрішньої довжини штанини проводяться по внутрішньому шву виробу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/seam-in.jpg" alt="Вимірювання довжини штанини по внутрішньому шву джинсів Levi’s"/>
                <p>
                    Заміри зовнішної довжини штанини проводяться аналогічно, але по зовнішньому шву виробу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/seam-out.jpg" alt="Вимірювання довжини штанини по зовнішньому шву джинсів Levi’s"/>
                <h3 className="fs-5">4. Штанина низ</h3>
                <p>
                    Необхідно заміряти від краю до краю рівно складену штанину внизу.
                </p>
                <img style={{ width: '100%', maxWidth: '600px'}} className="mb-3" src="/img/measurements/leg-opening.jpg" alt="Вимірювання ширини штанини внизу джинсів Levi’s"/>

                <h2 className="fs-5">Як посадка джинсів залежить від моделі</h2>
                <p>
                    Навіть якщо ви зробили точні заміри, правильний вибір джинсів залежить від моделі та крою. 
                    Нижче наведено популярні чоловічі моделі Levi’s з коротким описом посадки.
                </p>
                <ModelLinks onlyModels={topModels} />
                <p>
                    Використовуйте цей список, щоб швидко перейти до описів моделей і обрати джинси, 
                    які найкраще відповідатимуть вашим замірам.
                </p>
                
                <h3 className="fs-5">Корисні поради</h3>
                <ul>
                    <li>різні моделі Levi’s можуть сидіти по-різному</li>
                    <li>щільний денім може трохи розноситися з часом</li>
                    <li>можлива незначна усадка після першого прання</li>
                </ul>
                <p>
                    Тож перед покупкою радимо додатково перевірити <Link className="link-secondary" to="/tablytsia-rozmiriv-levis/">розмірну сітку джинсів Levi’s</Link>, 
                    щоб підібрати ідеальний розмір.  
                    Також дізнайтеся, <Link className="link-secondary" to="/yak-pereviryty-oryhinalnist-dzhynsiv-levis/">як перевірити оригінальність джинсів Levi’s</Link>, 
                    щоб не купити підробку.
                </p>
                <p>
                    Купити оригінальні джинси можна на <Link className="link-secondary" to="/">головній сторінці магазину</Link>.
                </p>

                <h2 className="fs-5">Чому варто звернутися до {Settings.shopName}</h2>
                <p>
                    <strong>{Settings.shopName}</strong> - український інтернет-магазин,
                    який з 2020 року спеціалізується <strong>виключно на чоловічих джинсах Levi’s</strong>.
                    Ми допомагаємо підібрати правильний розмір на основі реальних замірів.
                </p>
                <ul>
                    <li>надаємо точні заміри кожної пари</li>
                    <li>консультуємо щодо посадки та моделей</li>
                    <li>допомагаємо уникнути помилок з розміром</li>
                </ul>

                <h2 className="fs-5">Висновок</h2>
                <p>
                    Правильні заміри - найнадійніший спосіб обрати <strong>чоловічі джинси Levi’s</strong> онлайн.
                    Кілька хвилин вимірювань допоможуть отримати ідеальну посадку та комфорт.
                </p>
                <p>
                    Якщо ви шукаєте <strong>оригінальні чоловічі джинси Levi’s в Україні</strong>,
                    інтернет-магазин {Settings.shopName} завжди готовий допомогти з вибором.
                </p>
            </div>
            <RecomendedItems items={props.items.filter(x => topModels.includes(String(x.model)))} title="Рекомендуємо джинси Levi’s"/>
        </>
    );
}

export default Measurements;